import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  CssBaseline,
  TextField,
  Typography,
} from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { handleLogout } from '../../data/coreActions';
import { get, post } from '../../utils/ajax';
import { remoteRoutes } from '../../data/constants';
import Toast from '../../utils/Toast';
import elevateLogo from '../../assets/images/elevate-logo.png';

const CORAL = '#fe3a6a';
const ORANGE = '#fe8c45';

/**
 * Holds an account created since verification became mandatory on a
 * code-entry screen until the address is confirmed.
 *
 * Only accounts flagged at signup land here — everyone who already had an
 * account verifies at their own pace from Settings instead, and is never
 * interrupted. If the status check cannot be reached we let them through:
 * email is the part of this least under our control, and locking people
 * out of their course because a request failed would be the worse error.
 */
const VerifyEmailGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const [checked, setChecked] = useState(false);
  const [mustVerify, setMustVerify] = useState(false);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const load = useCallback(() => {
    get(
      remoteRoutes.emailVerification,
      (resp: any) => {
        setMustVerify(resp?.mustVerify === true);
        setEmail(resp?.email ?? '');
        setCooldown(Number(resp?.resendInSeconds) || 0);
      },
      () => setMustVerify(false),
      () => setChecked(true),
    );
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const resend = () => {
    setSending(true);
    post(
      remoteRoutes.emailVerificationSend,
      {},
      (resp: any) => {
        if (resp?.alreadyVerified) {
          setMustVerify(false);
          return;
        }
        setCooldown(60);
        Toast.info(`We have sent a new code to ${email}`);
      },
      (err: any) =>
        Toast.error(err?.response?.body?.message || 'Could not send the code'),
      () => setSending(false),
    );
  };

  const confirm = () => {
    setConfirming(true);
    post(
      remoteRoutes.emailVerificationConfirm,
      { code },
      () => {
        Toast.success('Email verified — welcome');
        setMustVerify(false);
      },
      (err: any) =>
        Toast.error(err?.response?.body?.message || "That code isn't right"),
      () => setConfirming(false),
    );
  };

  if (!checked) return null;
  if (!mustVerify) return <>{children}</>;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--ev-bgBody)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <CssBaseline />
      <div
        style={{
          width: '100%',
          maxWidth: 440,
          background: 'var(--ev-bgSurface)',
          borderRadius: 16,
          boxShadow: '0 10px 40px rgba(16,24,40,0.08)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            background: `linear-gradient(120deg, ${CORAL} 0%, ${ORANGE} 100%)`,
            color: '#fff',
            padding: '24px 26px',
          }}
        >
          <img
            src={elevateLogo}
            alt="era92 elevate"
            style={{
              height: 30,
              marginBottom: 10,
              filter: 'brightness(0) invert(1)',
            }}
          />
          <div style={{ fontSize: 20, fontWeight: 800 }}>
            Confirm your email
          </div>
          <div
            style={{
              fontSize: 14,
              opacity: 0.93,
              marginTop: 5,
              lineHeight: 1.5,
            }}
          >
            We sent a 6-digit code to <strong>{email}</strong>. Enter it below
            to finish setting up your account.
          </div>
        </div>

        <div style={{ padding: '22px 26px 26px' }}>
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            size="small"
            label="6-digit code"
            value={code}
            inputProps={{ inputMode: 'numeric', maxLength: 6 }}
            onChange={(e) =>
              setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
            }
          />

          <Button
            fullWidth
            variant="contained"
            disableElevation
            disabled={confirming || code.length < 6}
            onClick={confirm}
            style={{
              marginTop: 16,
              background: code.length === 6 ? CORAL : 'var(--ev-bgSubtle)',
              color: code.length === 6 ? '#fff' : 'var(--ev-textMuted)',
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 8,
              padding: '10px 0',
            }}
          >
            {confirming ? <CircularProgress size={20} /> : 'Verify my email'}
          </Button>

          <Typography
            variant="body2"
            style={{
              color: 'var(--ev-textMuted)',
              fontSize: 12.5,
              marginTop: 14,
              lineHeight: 1.6,
            }}
          >
            Check your spam folder if it has not arrived. The code expires in 15
            minutes.
          </Typography>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 10,
            }}
          >
            <Button
              onClick={resend}
              disabled={sending || cooldown > 0}
              style={{ textTransform: 'none', fontSize: 13 }}
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : 'Send another code'}
            </Button>
            <Button
              onClick={() => dispatch(handleLogout())}
              style={{
                textTransform: 'none',
                fontSize: 13,
                color: 'var(--ev-textMuted)',
              }}
            >
              Sign out
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailGate;
